import { z } from 'zod';

export const projectTypeSchema = z.enum(['thesis', 'journal']);
export type ProjectType = z.infer<typeof projectTypeSchema>;

export const protectedStatusSchema = z.enum(['draft', 'confirmed', 'protected']);
export type ProtectedStatus = z.infer<typeof protectedStatusSchema>;

const acceptedConsent = z.preprocess(
  (value) => value === true || value === 'on',
  z.literal(true, { error: 'Persetujuan diperlukan.' })
);

export const registerSchema = z.object({
  displayName: z.string().trim().min(2).max(80),
  email: z.string().trim().toLowerCase().email().max(254),
  password: z
    .string()
    .min(10, 'Kata sandi minimal 10 karakter.')
    .max(128)
    .regex(/[a-z]/, 'Tambahkan huruf kecil.')
    .regex(/[A-Z]/, 'Tambahkan huruf besar.')
    .regex(/[0-9]/, 'Tambahkan angka.'),
  termsAccepted: acceptedConsent,
  privacyAccepted: acceptedConsent
});

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1).max(128)
});

export const projectCreateSchema = z.object({
  type: projectTypeSchema,
  name: z.string().trim().min(3).max(160),
  licenseId: z.string().uuid().optional(),
  sourceProjectId: z.string().uuid().optional()
});

const listOfText = z.array(z.string().trim().min(1).max(1000)).max(30);

export const projectBrainSchema = z.object({
  studentName: z.string().trim().max(120).default(''),
  studentId: z.string().trim().max(40).default(''),
  campus: z.string().trim().max(160).default(''),
  faculty: z.string().trim().max(160).default(''),
  studyProgram: z.string().trim().max(160).default(''),
  degreeLevel: z.string().trim().max(40).default(''),
  academicYear: z.string().trim().max(20).default(''),
  researchTitle: z.string().trim().max(500).default(''),
  researchObject: z.string().trim().max(300).default(''),
  location: z.string().trim().max(300).default(''),
  method: z.string().trim().max(300).default(''),
  variables: listOfText.default([]),
  population: z.string().trim().max(500).default(''),
  sample: z.string().trim().max(500).default(''),
  researchPeriod: z.string().trim().max(200).default(''),
  researchQuestions: listOfText.default([]),
  objectives: listOfText.default([]),
  hypotheses: listOfText.default([]),
  indicators: listOfText.default([]),
  citationStyle: z.string().trim().min(2).max(80).default('APA 7th'),
  expectedRevision: z.number().int().positive().optional()
});
export type ProjectBrainInput = z.infer<typeof projectBrainSchema>;

export const protectedFactSchema = z.object({
  key: z.string().trim().min(1).max(100),
  value: z.unknown(),
  status: protectedStatusSchema
});

export const protectedFactsSchema = z
  .array(protectedFactSchema)
  .max(100)
  .superRefine((facts, ctx) => {
    const seen = new Set<string>();
    facts.forEach((fact, index) => {
      if (seen.has(fact.key)) {
        ctx.addIssue({
          code: 'custom',
          message: `Fakta ${fact.key} ganda.`,
          path: [index, 'key']
        });
      }
      seen.add(fact.key);
    });
  });

export const guidelineRulesSchema = z.object({
  fontBody: z.string().trim().min(2).max(80).default('Times New Roman'),
  fontSizePt: z.number().min(8).max(20).default(12),
  lineSpacing: z.number().min(1).max(3).default(1.5),
  marginsCm: z.object({
    top: z.number().min(1).max(8).default(4),
    right: z.number().min(1).max(8).default(3),
    bottom: z.number().min(1).max(8).default(3),
    left: z.number().min(1).max(8).default(4)
  }),
  chapterHeading: z.object({
    uppercase: z.boolean().default(true),
    bold: z.boolean().default(true),
    alignment: z.enum(['left', 'center', 'right']).default('center')
  }),
  subheading: z.object({
    bold: z.boolean().default(true),
    numbering: z.enum(['decimal', 'alpha', 'none']).default('decimal')
  }),
  tableCaption: z.object({ position: z.enum(['above', 'below']).default('above') }),
  figureCaption: z.object({ position: z.enum(['above', 'below']).default('below') }),
  pageNumbering: z
    .enum(['roman-front-arabic-body', 'arabic-all'])
    .default('roman-front-arabic-body'),
  citationStyle: z.string().trim().min(2).max(80).default('APA 7th'),
  chapterStructure: z.array(z.string().trim().min(1).max(160)).min(1).max(20)
});
export type GuidelineRules = z.infer<typeof guidelineRulesSchema>;

export const defaultGuidelineRules: GuidelineRules = {
  fontBody: 'Times New Roman',
  fontSizePt: 12,
  lineSpacing: 1.5,
  marginsCm: { top: 4, right: 3, bottom: 3, left: 4 },
  chapterHeading: { uppercase: true, bold: true, alignment: 'center' },
  subheading: { bold: true, numbering: 'decimal' },
  tableCaption: { position: 'above' },
  figureCaption: { position: 'below' },
  pageNumbering: 'roman-front-arabic-body',
  citationStyle: 'APA 7th',
  chapterStructure: [
    'Pendahuluan',
    'Tinjauan Pustaka',
    'Metode Penelitian',
    'Hasil dan Pembahasan',
    'Penutup'
  ]
};

export const guidelineRuleSetSchema = z.object({
  name: z.string().trim().min(3).max(160),
  guidelineFileId: z.string().uuid().nullable().optional(),
  rules: guidelineRulesSchema
});

export const sectionCreateSchema = z.object({
  chapterId: z.string().uuid(),
  title: z.string().trim().min(2).max(300),
  parentSectionId: z.string().uuid().nullable().optional(),
  level: z.number().int().min(1).max(4).default(1)
});

export const sectionUpdateSchema = z.object({
  title: z.string().trim().min(2).max(300),
  expectedRevision: z.number().int().positive()
});

export const blockCreateSchema = z.object({
  sectionId: z.string().uuid(),
  type: z
    .enum(['paragraph', 'citation', 'table', 'figure', 'equation', 'attachment'])
    .default('paragraph'),
  content: z.record(z.string(), z.unknown()).default({ text: '' })
});

export const blockUpdateSchema = z.object({
  content: z.record(z.string(), z.unknown()),
  expectedRevision: z.number().int().positive(),
  reason: z.enum(['autosave', 'manual', 'restore']).default('autosave')
});

export const snapshotSchema = z.object({
  label: z.string().trim().min(2).max(120),
  kind: z.enum(['manual', 'automatic']).default('manual')
});

export type SemanticBlock = {
  id: string;
  type: 'paragraph' | 'citation' | 'table' | 'figure' | 'equation' | 'attachment';
  chapterId: string;
  sectionId: string;
  position: number;
  content: Record<string, unknown>;
};

export type PresentationRule = {
  fontFamily: string;
  fontSizePt: number;
  lineSpacing: number;
  captionPosition?: 'above' | 'below';
};

export type AITaskType =
  | 'guideline_extract'
  | 'source_summarize'
  | 'reference_relevance'
  | 'rewrite_small'
  | 'paraphrase'
  | 'section_outline'
  | 'section_generate'
  | 'section_revise'
  | 'thesis_consistency_review'
  | 'evidence_interpretation'
  | 'revision_parse'
  | 'revision_apply_draft'
  | 'journal_outline'
  | 'journal_guideline_extract'
  | 'journal_section_generate'
  | 'final_academic_review';

// FINAL ALIGNMENT A: trusted AI grounding
export type AIContext = {
  projectId: string;
  projectBrain: ProjectBrainInput;
  protectedFacts: z.infer<typeof protectedFactsSchema>;
  terminology: Array<{ term: string; alternatives: string[] }>;
  writingProfile: Record<string, unknown>;
  guideline: GuidelineRules | null;
  relevantBlocks: SemanticBlock[];
  trustedReferences?: Array<{
    id: string;
    title: string;
    authors: string[];
    publicationYear: number | null;
    doi: string | null;
    url: string | null;
  }>;
  trustedEvidence?: Array<{
    id: string;
    kind: string;
    title: string;
    summary: string;
  }>;
};

export interface AIProvider {
  readonly key: string;
  execute(task: AITaskType, context: AIContext, input: unknown): Promise<unknown>;
}

export interface AIValidationPipeline {
  validate(
    task: AITaskType,
    context: AIContext,
    candidate: unknown
  ): Promise<{ valid: boolean; issues: string[] }>;
}

export interface ProjectContextBuilder {
  build(projectId: string, relevantObjectIds: string[]): Promise<AIContext>;
}

export interface DocumentCompiler {
  compile(input: {
    projectId: string;
    scope: 'chapter' | 'through-chapter' | 'proposal' | 'full' | 'selection';
    target: 'docx' | 'pdf';
    activeRuleSetId: string;
  }): Promise<{ objectKey: string; sha256: string; version: number }>;
}

export const aiTaskTypeSchema = z.enum([
  'guideline_extract',
  'source_summarize',
  'reference_relevance',
  'rewrite_small',
  'paraphrase',
  'section_outline',
  'section_generate',
  'section_revise',
  'thesis_consistency_review',
  'evidence_interpretation',
  'revision_parse',
  'revision_apply_draft',
  'journal_outline',
  'journal_guideline_extract',
  'journal_section_generate',
  'final_academic_review'
]);

export const aiCandidateCreateSchema = z.object({
  taskType: aiTaskTypeSchema,
  targetType: z.enum(['project', 'chapter', 'section', 'block', 'guideline', 'journal']),
  targetId: z.string().uuid().nullable().optional(),
  instruction: z.string().trim().min(3).max(6000),
  relevantObjectIds: z.array(z.string().uuid()).max(30).default([]),
  idempotencyKey: z.string().trim().min(8).max(120).optional()
});

export const aiCandidateApplySchema = z.object({
  expectedRevision: z.number().int().positive().optional()
});

export const referenceTypeSchema = z.enum([
  'journal',
  'book',
  'report',
  'statistic',
  'news',
  'website',
  'user_file'
]);

export const referenceCreateSchema = z.object({
  type: referenceTypeSchema,
  title: z.string().trim().min(3).max(1000),
  authors: z.array(z.string().trim().min(1).max(240)).max(100).default([]),
  publicationYear: z.number().int().min(1000).max(2200).nullable().optional(),
  doi: z.string().trim().max(300).nullable().optional(),
  url: z.string().url().max(2000).nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).default({})
});

export const referenceSearchSchema = z.object({
  query: z.string().trim().min(3).max(500),
  provider: z.enum(['crossref', 'openalex']).default('crossref'),
  limit: z.number().int().min(1).max(20).default(8)
});

export const evidenceCreateSchema = z.object({
  attachmentId: z.string().uuid().nullable().optional(),
  kind: z.enum([
    'dataset',
    'analysis',
    'interview',
    'experiment',
    'official_statistic',
    'user_result'
  ]),
  title: z.string().trim().min(3).max(500),
  summary: z.string().trim().max(5000).default(''),
  data: z.record(z.string(), z.unknown()).default({}),
  status: z.enum(['draft', 'confirmed', 'protected']).default('draft')
});

// FINAL ALIGNMENT A: evidence lifecycle
export const evidenceStatusUpdateSchema = z.object({
  status: z.enum(['confirmed', 'protected'])
});

export const revisionSessionSchema = z
  .object({
    inputType: z.enum(['text', 'docx', 'pdf', 'image']).default('text'),
    sourceText: z.string().trim().max(30000).default(''),
    attachmentId: z.string().uuid().nullable().optional()
  })
  .superRefine((value, ctx) => {
    if (value.inputType === 'text' && value.sourceText.length < 3) {
      ctx.addIssue({
        code: 'custom',
        message: 'Catatan revisi minimal 3 karakter.',
        path: ['sourceText']
      });
    }
    if (value.inputType !== 'text' && !value.attachmentId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Pilih berkas catatan revisi.',
        path: ['attachmentId']
      });
    }
  });

export const revisionTaskApplySchema = z.object({
  proposedText: z.string().trim().min(1).max(50000),
  expectedRevision: z.number().int().positive(),
  targetBlockId: z.string().uuid().optional()
});

export const exportCreateSchema = z.object({
  format: z.enum(['docx', 'pdf']).default('docx'),
  scope: z.enum(['chapter', 'through-chapter', 'proposal', 'full', 'selection']).default('full'),
  chapterId: z.string().uuid().nullable().optional(),
  selectionIds: z.array(z.string().uuid()).max(100).default([])
});

export const journalCreateSchema = z.object({
  entryPath: z.enum(['active_project', 'uploaded_thesis']),
  sourceProjectId: z.string().uuid().nullable().optional(),
  sourceAttachmentId: z.string().uuid().nullable().optional(),
  title: z.string().trim().max(500).default(''),
  settings: z.record(z.string(), z.unknown()).default({})
});

export const journalGuidelineRulesSchema = z.object({
  name: z.string().trim().max(200).default('Author Guidelines'),
  language: z.string().trim().max(80).default('Bahasa Indonesia'),
  citationStyle: z.string().trim().max(80).default('APA 7th'),
  fontBody: z.string().trim().max(80).default('Times New Roman'),
  fontSizePt: z.number().min(8).max(20).default(11),
  lineSpacing: z.number().min(1).max(3).default(1.15),
  marginsCm: z
    .object({
      top: z.number().min(1).max(8).default(2.5),
      right: z.number().min(1).max(8).default(2.5),
      bottom: z.number().min(1).max(8).default(2.5),
      left: z.number().min(1).max(8).default(2.5)
    })
    .default({ top: 2.5, right: 2.5, bottom: 2.5, left: 2.5 }),
  maxWords: z.number().int().min(500).max(30000).nullable().default(null),
  abstractMinWords: z.number().int().min(50).max(1000).nullable().default(null),
  abstractMaxWords: z.number().int().min(50).max(1500).nullable().default(null),
  keywordMin: z.number().int().min(1).max(20).nullable().default(null),
  keywordMax: z.number().int().min(1).max(30).nullable().default(null),
  requiredSections: z
    .array(z.string().trim().min(1).max(160))
    .min(1)
    .max(30)
    .default([
      'Judul',
      'Abstrak',
      'Abstract',
      'Kata Kunci',
      'Pendahuluan',
      'Metode',
      'Hasil dan Pembahasan',
      'Kesimpulan',
      'Daftar Pustaka'
    ]),
  tableNotes: z.string().trim().max(1000).default(''),
  figureNotes: z.string().trim().max(1000).default(''),
  authorNotes: z.string().trim().max(2000).default('')
});
export type JournalGuidelineRules = z.infer<typeof journalGuidelineRulesSchema>;

export const journalGuidelineExtractSchema = z.object({
  attachmentId: z.string().uuid()
});

export const journalSectionCandidateSchema = z.object({
  instruction: z.string().trim().max(3000).default('Susun bagian ini secara akademik dan ringkas.'),
  complexity: z.number().min(0).max(10).default(5),
  idempotencyKey: z.string().trim().min(8).max(160).optional()
});

export const journalSectionApplySchema = z.object({
  candidateId: z.string().uuid(),
  expectedRevision: z.number().int().positive(),
  proposedText: z.string().trim().min(1).max(50000).optional()
});

export const foundationUpdateSchema = z.object({
  facts: z
    .array(
      z.object({
        id: z.string().uuid(),
        status: z.enum(['draft', 'confirmed', 'protected'])
      })
    )
    .max(100)
    .default([]),
  terminology: z
    .array(
      z.object({
        preferredTerm: z.string().trim().min(1).max(200),
        alternatives: z.array(z.string().trim().min(1).max(200)).max(20).default([]),
        definition: z.string().trim().max(1000).default(''),
        status: z.enum(['draft', 'confirmed', 'protected']).default('confirmed')
      })
    )
    .max(100)
    .default([]),
  writingProfile: z.object({
    name: z.string().trim().min(2).max(100).default('Natural Academic'),
    language: z.string().trim().min(2).max(50).default('Bahasa Indonesia'),
    formality: z.string().trim().min(2).max(100).default('academic-natural'),
    sentenceRhythm: z.string().trim().min(2).max(100).default('balanced'),
    transitionStyle: z.string().trim().min(2).max(100).default('explicit'),
    depthLevel: z.string().trim().min(2).max(100).default('undergraduate'),
    preferredTerms: z.array(z.string().trim().min(1).max(200)).max(100).default([]),
    avoidedTerms: z.array(z.string().trim().min(1).max(200)).max(100).default([]),
    sampleText: z.string().max(10000).default(''),
    status: z.enum(['draft', 'confirmed', 'active']).default('active')
  })
});
